/**
 * Flexible date parser — accepts many common formats a user might type:
 *   "10/1/2025"      MM/DD/YYYY (US default)
 *   "10-1-2025"      MM-DD-YYYY
 *   "2025-10-01"     YYYY-MM-DD (ISO)
 *   "1 Jan 2026"     D Mon YYYY
 *   "1-Jan-2026"     D-Mon-YYYY
 *   "Jan 1, 2026"    Mon D, YYYY
 *   "January 1 2026" Full month name
 *   "10012025"       MMDDYYYY compact
 *   "20251001"       YYYYMMDD compact
 *
 * Returns a Date or null if parsing fails. Validates that the resulting month
 * matches (catches e.g. Feb 30).
 */

const MN: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  january: 0, february: 1, march: 2, april: 3, june: 5, july: 6, august: 7,
  september: 8, october: 9, november: 10, december: 11,
};

export function parseFlexDate(str: string | null | undefined): Date | null {
  if (!str || !str.trim()) return null;
  const s = str.trim();

  // 1: "1 Jan 2026"  /  "1-Jan-2026"
  const m1 = s.match(/^(\d{1,2})[\s\-/]+([a-zA-Z]+)[\s\-/,]+(\d{2,4})$/);
  if (m1) {
    const mon = MN[m1[2]!.toLowerCase()];
    if (mon !== undefined) {
      let y = parseInt(m1[3]!, 10);
      if (y < 100) y += 2000;
      const dt = new Date(y, mon, parseInt(m1[1]!, 10));
      if (dt.getMonth() === mon) return dt;
    }
  }

  // 2: "Jan 1 2026"  /  "January 1, 2026"
  const m2 = s.match(/^([a-zA-Z]+)[\s\-/,]+(\d{1,2})[\s\-/,]+(\d{2,4})$/);
  if (m2) {
    const mon = MN[m2[1]!.toLowerCase()];
    if (mon !== undefined) {
      let y = parseInt(m2[3]!, 10);
      if (y < 100) y += 2000;
      const dt = new Date(y, mon, parseInt(m2[2]!, 10));
      if (dt.getMonth() === mon) return dt;
    }
  }

  // 3: numeric separators (slash, dash, dot, space) — handles US default + ISO.
  const parts = s.split(/[\s/\-.]+/).filter(Boolean);
  if (parts.length === 3) {
    const a = parseInt(parts[0]!, 10);
    const b = parseInt(parts[1]!, 10);
    const c = parseInt(parts[2]!, 10);
    let y: number, mo: number, d: number;
    if (parts[0]!.length === 4) {
      y = a; mo = b - 1; d = c;                 // YYYY-MM-DD
    } else if (parts[2]!.length >= 4 || c >= 1000) {
      y = c;                                    // M/D/YYYY (or D/M when day > 12)
      if (a > 12) { d = a; mo = b - 1; }
      else        { mo = a - 1; d = b; }
    } else {
      y = c < 100 ? c + 2000 : c;               // M/D/YY → 20YY
      mo = a - 1; d = b;
    }
    if (!isNaN(y) && mo >= 0 && mo <= 11 && d >= 1 && d <= 31) {
      const dt = new Date(y, mo, d);
      if (dt.getMonth() === mo) return dt;
    }
  }

  // 4: 8-digit compact: MMDDYYYY or YYYYMMDD.
  const digits = s.replace(/\D/g, "");
  if (digits.length === 8) {
    const mo = parseInt(digits.slice(0, 2), 10) - 1;
    const d = parseInt(digits.slice(2, 4), 10);
    const y = parseInt(digits.slice(4, 8), 10);
    if (mo >= 0 && mo <= 11 && d >= 1 && d <= 31) {
      const dt = new Date(y, mo, d);
      if (dt.getMonth() === mo) return dt;
    }
    const y2 = parseInt(digits.slice(0, 4), 10);
    const mo2 = parseInt(digits.slice(4, 6), 10) - 1;
    const d2 = parseInt(digits.slice(6, 8), 10);
    if (mo2 >= 0 && mo2 <= 11 && d2 >= 1 && d2 <= 31) {
      const dt = new Date(y2, mo2, d2);
      if (dt.getMonth() === mo2) return dt;
    }
  }

  // 5: native JS Date fallback.
  const native = new Date(s);
  if (!isNaN(native.getTime())) return native;

  return null;
}
